import { useCallback, useEffect, useState } from 'react';
import axiosInstance from '../axiosInstance';

const BASE = '/hotel-mapping';

// API Functions
export const getHotelMappings = (masterHotelId) =>
  axiosInstance.get(`${BASE}/${masterHotelId}`).then((res) => res.data);

export const createHotelMapping = (payload) =>
  axiosInstance.post(BASE, payload).then((res) => res.data);

export const updateHotelMapping = (mappingId, payload) =>
  axiosInstance.put(`${BASE}/${mappingId}`, payload).then((res) => res.data);

export const verifyHotelMapping = (mappingId, userId = null) =>
  axiosInstance.patch(`${BASE}/${mappingId}/verify`, { userId }).then((res) => res.data);

export const deleteHotelMapping = (mappingId) =>
  axiosInstance.delete(`${BASE}/${mappingId}`).then((res) => res.data);

export const getUnmappedHotels = (params = {}) =>
  axiosInstance.get(`${BASE}/unmapped`, { params }).then((res) => res.data);

// Master hotels that have at least one live mapping, each with its mappings.
// Params: page, limit, search, countryCode, destinationCode, zoneCode, supplierId
export const getMappedHotels = (params = {}) =>
  axiosInstance.get(`${BASE}/mapped`, { params }).then((res) => res.data);

// Suppliers that have Active API credentials — the supplier dropdown options.
export const getApiSuppliers = () =>
  axiosInstance.get('/supplier-api/suppliers').then((res) => res.data);

export const getMappingStats = () =>
  axiosInstance.get(`${BASE}/stats`).then((res) => res.data);

export const autoMatchSupplierHotels = (payload) =>
  axiosInstance.post(`${BASE}/auto-match`, payload).then((res) => res.data);

export const fetchAndMatchSupplierHotels = (payload, signal) =>
  axiosInstance.post('/supplier-api/fetch-and-match', payload, { timeout: 120000, signal }).then((res) => res.data);

// Data Transformation Functions
export const mapApiMappingToUi = (apiMapping) => {
  if (!apiMapping) return null;

  return {
    id: apiMapping.id,
    supplierId: apiMapping.supplierId,
    supplierName: apiMapping.supplierName,
    supplierFullName: apiMapping.supplierFullName,
    supplierCategory: apiMapping.supplierCategory,
    supplierHotelCode: apiMapping.supplierHotelCode,
    supplierHotelName: apiMapping.supplierHotelName,
    matchScore: apiMapping.matchScore,
    matchMethod: apiMapping.matchMethod,
    mappingStatus: apiMapping.mappingStatus,
    isActive: apiMapping.isActive,
    priority: apiMapping.priority,
    verifiedAt: apiMapping.verifiedAt,
  };
};

export const mapApiMasterHotelToUi = (apiHotel) => {
  if (!apiHotel) return null;

  return {
    id: apiHotel.id,
    hotelCode: apiHotel.hotelCode,
    name: apiHotel.name || apiHotel.hotelName || "Unknown Hotel",
    city: apiHotel.city || "",
    stars: Number(apiHotel.stars || apiHotel.starRating || 0),
    zone: apiHotel.address || apiHotel.zone || "",
    country: apiHotel.country || apiHotel.countryCode || "",
    countryCode: apiHotel.countryCode,
    latitude: apiHotel.latitude,
    longitude: apiHotel.longitude,
  };
};

export const mapApiSupplierSuggestionToUi = (apiSuggestion) => {
  if (!apiSuggestion) return null;

  return {
    // Composite id — with "All suppliers", two suppliers can share a hotel code.
    id: `${apiSuggestion.supplierId || 0}:${apiSuggestion.code}`,
    supplierCode: apiSuggestion.code,
    name: apiSuggestion.name,
    city: apiSuggestion.city,
    district: apiSuggestion.district,
    province: apiSuggestion.province,
    address: apiSuggestion.address,
    country: apiSuggestion.countryCode,
    latitude: apiSuggestion.latitude,
    longitude: apiSuggestion.longitude,
    stars: apiSuggestion.stars || 0,
    category: apiSuggestion.category,
    region: apiSuggestion.region,
    resort: apiSuggestion.resort,
    boards: apiSuggestion.boards || [],
    matchScore: apiSuggestion.matchScore || 0,
    scores: apiSuggestion.scores || {},
    distance: apiSuggestion.distance || 0,
    // Real supplier identity from the API (tagged per supplier by the backend).
    supplier: apiSuggestion.supplierName || 'Supplier',
    supplierId: apiSuggestion.supplierId,
    // Existing-mapping state so the UI can show mapped suggestions as done.
    mappedToHotelbedsId: apiSuggestion.mappedToMasterHotelId ?? null,
    mappedToHotelbedsName: apiSuggestion.mappedToMasterHotelName ?? null,
    mappedToThisHotel: apiSuggestion.mappedToThisHotel ?? false,
    // Identity of the existing mapping row — lets the UI clear the state of
    // exactly the suggestion that was unmapped, not every one on this hotel.
    mappedMappingId: apiSuggestion.mappedMappingId ?? null,
  };
};

export const mapUiMappingToApi = (uiMapping) => {
  return {
    masterHotelId: uiMapping.masterHotelId,
    supplierId: uiMapping.supplierId,
    supplierHotelCode: uiMapping.supplierHotelCode,
    supplierHotelName: uiMapping.supplierHotelName,
    matchScore: uiMapping.matchScore || 100,
    matchMethod: uiMapping.matchMethod || 'Manual',
    mappingStatus: uiMapping.mappingStatus || 'Pending',
    priority: uiMapping.priority || 0,
    latitude: uiMapping.latitude,
    longitude: uiMapping.longitude,
    city: uiMapping.city,
    countryCode: uiMapping.countryCode,
    supplierMetadata: uiMapping.supplierMetadata,
  };
};

// Custom Hooks
export const useHotelMappings = (masterHotelId) => {
  const [data, setData] = useState({
    masterHotel: null,
    mappings: [],
    stats: {
      totalMapped: 0,
      verified: 0,
      pending: 0,
    },
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchMappings = useCallback(async () => {
    if (!masterHotelId) return;

    setLoading(true);
    setError(null);
    try {
      const response = await getHotelMappings(masterHotelId);
      
      if (response.success) {
        const masterHotel = mapApiMasterHotelToUi(response.data.masterHotel);
        const mappings = (response.data.mappings || []).map(mapApiMappingToUi);
        
        setData({
          masterHotel,
          mappings,
          stats: response.data.stats || {
            totalMapped: 0,
            verified: 0,
            pending: 0,
          },
        });
      } else {
        setError(response.message || 'Failed to fetch hotel mappings');
      }
    } catch (err) {
      const message = 
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Failed to fetch hotel mappings';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [masterHotelId]);

  useEffect(() => {
    fetchMappings();
  }, [fetchMappings]);

  const refetch = useCallback(() => {
    fetchMappings();
  }, [fetchMappings]);

  return { data, loading, error, refetch };
};

export const useCreateHotelMapping = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const createMapping = useCallback(async (mappingData) => {
    setLoading(true);
    setError(null);
    try {
      const payload = mapUiMappingToApi(mappingData);
      const response = await createHotelMapping(payload);
      
      if (response.success) {
        return { success: true, data: response.data };
      } else {
        setError(response.message || 'Failed to create mapping');
        return { success: false, error: response.message };
      }
    } catch (err) {
      const message = 
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Failed to create mapping';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, []);

  return { createMapping, loading, error };
};

export const useUpdateHotelMapping = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const updateMapping = useCallback(async (mappingId, updateData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await updateHotelMapping(mappingId, updateData);
      
      if (response.success) {
        return { success: true, data: response.data };
      } else {
        setError(response.message || 'Failed to update mapping');
        return { success: false, error: response.message };
      }
    } catch (err) {
      const message = 
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Failed to update mapping';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, []);

  return { updateMapping, loading, error };
};

export const useVerifyHotelMapping = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const verifyMapping = useCallback(async (mappingId, userId = null) => {
    setLoading(true);
    setError(null);
    try {
      const response = await verifyHotelMapping(mappingId, userId);
      
      if (response.success) {
        return { success: true, data: response.data };
      } else {
        setError(response.message || 'Failed to verify mapping');
        return { success: false, error: response.message };
      }
    } catch (err) {
      const message = 
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Failed to verify mapping';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, []);

  return { verifyMapping, loading, error };
};

export const useDeleteHotelMapping = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const deleteMapping = useCallback(async (mappingId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await deleteHotelMapping(mappingId);
      
      if (response.success) {
        return { success: true };
      } else {
        setError(response.message || 'Failed to delete mapping');
        return { success: false, error: response.message };
      }
    } catch (err) {
      const message = 
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Failed to delete mapping';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, []);

  return { deleteMapping, loading, error };
};

export const useMappingStats = () => {
  const [data, setData] = useState({
    totalMappedHotels: 0,
    totalMappings: 0,
    verifiedMappings: 0,
    pendingMappings: 0,
    autoMatched: 0,
    avgMatchScore: 0,
    suppliersUsed: 0,
    totalHotels: 0,
    unmappedHotels: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getMappingStats();
      
      if (response.success) {
        setData(response.data || {});
      } else {
        setError(response.message || 'Failed to fetch mapping stats');
      }
    } catch (err) {
      const message = 
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Failed to fetch mapping stats';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const refetch = useCallback(() => {
    fetchStats();
  }, [fetchStats]);

  return { data, loading, error, refetch };
};
